import {
  BarChart3,
  ClipboardList,
  FileText,
  Menu,
  Plug,
  Puzzle,
  Send,
  Settings as SettingsIcon,
  Trash2,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../auth/useAuth";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import { ManualReviewAlert } from "./ManualReviewAlert";

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  adminOnly?: boolean;
};

type NavGroup = {
  title: string;
  items: NavItem[];
};

const NAV_GROUPS: NavGroup[] = [
  {
    title: "Аналитика",
    items: [
      { to: "/documents", label: "Документы", icon: FileText },
      { to: "/dispatches", label: "Отправки", icon: Send },
      { to: "/reports", label: "Отчёты", icon: BarChart3 },
    ],
  },
  {
    title: "Интеграции",
    items: [
      { to: "/bitrix", label: "Bitrix24", icon: Plug },
      {
        to: "/extension-health",
        label: "Браузерное расширение",
        icon: Puzzle,
        adminOnly: true,
      },
    ],
  },
  {
    title: "Администрирование",
    items: [
      { to: "/users", label: "Сотрудники", icon: Users },
      {
        to: "/privacy",
        label: "Удаление данных",
        icon: Trash2,
        adminOnly: true,
      },
      {
        to: "/audit-log",
        label: "Аудит действий",
        icon: ClipboardList,
        adminOnly: true,
      },
      { to: "/settings", label: "Настройки", icon: SettingsIcon },
    ],
  },
];

export function AppLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const groups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.adminOnly || isAdmin),
  })).filter((group) => group.items.length > 0);

  // Мобильный drawer. На lg+ сайдбар sticky, никакие state-флаги не нужны;
  // на узких экранах сайдбар скрыт, его открывает кнопка-«гамбургер»
  // в шапке. Закрываем drawer автоматически при смене маршрута, чтобы
  // переход по ссылке возвращал пользователя на контент.
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // ESC закрывает drawer; пока drawer открыт — блокируем скролл body,
  // чтобы клиент не «уезжал» при скролле списка.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [drawerOpen]);

  const sidebar = (
    <>
      <div className="px-5 py-5 flex items-center justify-between gap-2">
        <Link
          to="/"
          aria-label="tracelink — на главную"
          className="inline-flex"
        >
          <Logo />
        </Link>
        <button
          type="button"
          onClick={() => setDrawerOpen(false)}
          aria-label="Закрыть меню"
          className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accentWarm/10"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-2 flex flex-col gap-5 text-sm">
        {groups.map((group) => (
          <div key={group.title} className="flex flex-col gap-1">
            <div className="px-3 pb-1 font-display text-[10px] font-bold uppercase tracking-widest text-muted-foreground/80">
              {group.title}
            </div>
            {group.items.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    "group relative flex items-center gap-3 pl-4 pr-3 py-2 rounded-xl text-foreground/75 transition-all",
                    "hover:text-foreground hover:bg-accentWarm/10",
                    isActive &&
                      "bg-accentWarm/10 text-foreground font-medium hover:bg-accentWarm/15",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {/* Тёплая «прожилка» слева у активного раздела —
                     * визуальный якорь, заменяющий тяжёлый neu-inset. */}
                    <span
                      aria-hidden="true"
                      className={cn(
                        "absolute left-1 top-1.5 bottom-1.5 w-[3px] rounded-full transition-opacity",
                        isActive
                          ? "bg-accentWarm opacity-100"
                          : "opacity-0",
                      )}
                    />
                    <Icon
                      className={cn(
                        "h-4 w-4 shrink-0 transition-colors",
                        isActive
                          ? "text-accentWarm"
                          : "group-hover:text-accentWarm/80",
                      )}
                      aria-hidden="true"
                    />
                    <span className="truncate">{label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="px-4 py-4 flex flex-col gap-2 text-sm">
        <span
          className="font-mono text-xs text-muted-foreground truncate"
          title={user?.email ?? ""}
        >
          {user?.email}
        </span>
        <Button
          size="sm"
          variant="ghost"
          className="justify-start px-2 -mx-1 hover:neu-raised-sm"
          onClick={() => logout()}
        >
          Выйти
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex neu-canvas">
      {/* Десктопный сайдбар: статично слева. */}
      <aside className="hidden lg:flex w-60 shrink-0 sticky top-0 h-screen flex-col">
        {sidebar}
      </aside>

      {/* Мобильный drawer: появляется по кнопке. Бэкдроп ловит клик
       * вне меню. Z-индексы — выше шапки, чтобы drawer перекрывал
       * стопку контента. */}
      {drawerOpen && (
        <div
          aria-hidden="true"
          className="lg:hidden fixed inset-0 z-40 bg-black/40"
          onClick={() => setDrawerOpen(false)}
        />
      )}
      <aside
        className={cn(
          "lg:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] flex flex-col neu-canvas shadow-2xl",
          "transition-transform duration-200 ease-out",
          drawerOpen ? "translate-x-0" : "-translate-x-full",
        )}
        aria-hidden={!drawerOpen}
        data-testid="mobile-sidebar"
      >
        {sidebar}
      </aside>

      <div className="flex-1 min-w-0 flex flex-col bg-background">
        {/* Шапка с гамбургером — только на мобиле/планшете. */}
        <header className="lg:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-background/95 backdrop-blur border-b">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Открыть меню"
            data-testid="open-mobile-sidebar"
            className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-foreground/80 hover:text-foreground hover:bg-accentWarm/10"
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </button>
          <Link
            to="/"
            aria-label="tracelink — на главную"
            className="inline-flex"
          >
            <Logo />
          </Link>
        </header>
        <ManualReviewAlert />
        <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
